import type { CodeAttachmentRef } from '@/lib/code-sessions/attachment-types';
import type { SessionGitStatus } from '@/lib/code-sessions/git';
import type { CodeSkillInventory } from '@/lib/code-sessions/skills';
import type { CodeSessionEvent } from '@/lib/code-sessions/types';
import type {
  WorkspaceChangesResult,
  WorkspaceDiffResult,
  WorkspaceFileResult,
  WorkspaceSearchResult,
} from '@/lib/code-sessions/workspace-types';

import type {
  CodePromptAttachment,
  CodePromptSubmission,
  CodeSessionRow,
  CodeSessionSummary,
  CodeSessionTurn,
  CreateCodeSessionInput,
  CreateCodeSessionTarget,
  SessionResponse,
} from '../types';
import type {
  CodeRuntime,
  ReadOptions,
  SessionConversation,
  SessionRecord,
  SessionWorkspace,
} from './types';

import { normalizeWorkspacePath } from '../workspace-keys';
import { CodeSessionRequestError, isRefusal, UnconfirmedPromptError } from './request-error';
import { placeBySendTime, UnconfirmedSends } from './unconfirmed-sends';

export async function codeSessionRequest<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, init);
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    throw new CodeSessionRequestError(
      body?.error ?? 'Code session request failed',
      response.status,
    );
  }
  return body as T;
}

/** A JSON POST for `codeSessionRequest`. */
export const jsonBody = (body: unknown): RequestInit => ({
  method: 'POST',
  headers: { 'content-type': 'application/json' },
  body: JSON.stringify(body),
});

/** A read's `fetch` init: only its signal, and none at all for a read nobody can abandon. */
const readInit = ({ signal }: ReadOptions = {}): RequestInit | undefined => signal && { signal };

/** What a prompt request sends for each file; the server reads the rest from storage. */
function attachmentRefs(
  attachments: readonly Pick<CodePromptAttachment, 'id' | 'name'>[],
): CodeAttachmentRef[] {
  return attachments.map(({ id, name }) => ({ id, name }));
}

export const NEVER_GOT_MESSAGE = 'The agent never got this message';

type UnsentCloudTurn = Omit<CodeSessionTurn, 'index'>;

const unconfirmedSends = new Map<string, UnconfirmedSends<UnsentCloudTurn>>();

function unconfirmedSendsOf(sessionId: string): UnconfirmedSends<UnsentCloudTurn> {
  let sends = unconfirmedSends.get(sessionId);
  if (sends === undefined) {
    sends = new UnconfirmedSends();
    unconfirmedSends.set(sessionId, sends);
  }
  return sends;
}

function unsentTurn(
  input: CodePromptSubmission & { turnId: string; authorProfileId?: string; sentAt: string },
): UnsentCloudTurn {
  return {
    id: input.turnId,
    delivery: input.delivery,
    inboxId: null,
    status: 'failed',
    prompt: input.text,
    authoredText: input.authoredText ?? null,
    createdAt: input.sentAt,
    skills: input.skills,
    promptAttachments: input.attachments,
    startedAt: null,
    endedAt: input.sentAt,
    errorMessage: NEVER_GOT_MESSAGE,
    unsent: true,
    changedFiles: null,
    discardedAt: null,
    checkInReason: null,
    messageFrom: null,
    authorProfileId: input.authorProfileId ?? '',
  };
}

function placeUnsentTurns(
  turns: readonly CodeSessionTurn[],
  unsent: readonly UnsentCloudTurn[],
): CodeSessionTurn[] {
  const placed: CodeSessionTurn[] = [];
  let run: UnsentCloudTurn[] = [];
  // Each run of failed sends is numbered evenly between its saved neighbours, so none is a whole
  // number: the server numbers a turn whole, and a turn saved after the page gave up on it then
  // announces an index no row has, which starts a read.
  const placeRun = (next: number | undefined) => {
    const low = placed.at(-1)?.index ?? (next ?? 0) - 1;
    const high = next ?? low + 1;
    run.forEach((row, at) => {
      placed.push({ ...row, index: low + ((high - low) * (at + 1)) / (run.length + 1) });
    });
    run = [];
  };
  for (const turn of placeBySendTime(
    [...turns].sort((a, b) => a.index - b.index),
    unsent,
  )) {
    if ('index' in turn) {
      placeRun(turn.index);
      placed.push(turn);
    } else {
      run.push(turn);
    }
  }
  placeRun(undefined);
  return placed;
}

async function withUnsentSends<T extends { session: CodeSessionRow } | null>(
  unconfirmed: UnconfirmedSends<UnsentCloudTurn>,
  read: () => Promise<T>,
): Promise<T> {
  const checkpoint = unconfirmed.checkpoint();
  const response = await read();
  if (response === null) {
    return response;
  }
  const turns = response.session.turns;
  unconfirmed.settle(turns.map((turn) => turn.id));
  const unsent = unconfirmed.before(checkpoint);
  if (unsent.length === 0) {
    return response;
  }
  return {
    ...response,
    session: { ...response.session, turns: placeUnsentTurns(turns, unsent) },
  };
}

/** `url` is the session's API path (`/api/code-sessions/<id>`). */
function cloudConversation(
  url: string,
  unconfirmed: UnconfirmedSends<UnsentCloudTurn>,
): SessionConversation {
  return {
    snapshot: (options) =>
      withUnsentSends(unconfirmed, () =>
        codeSessionRequest<SessionResponse>(url, readInit(options)),
      ),
    prefetch: (options) =>
      withUnsentSends(unconfirmed, () =>
        codeSessionRequest<SessionResponse | null>(`${url}?prefetch=1`, readInit(options)),
      ),
    retryTranscript: (options) =>
      withUnsentSends(unconfirmed, () =>
        codeSessionRequest<SessionResponse>(`${url}?retryTranscript=1`, readInit(options)),
      ),
    replay: (afterSeq, options) =>
      codeSessionRequest<{ events: CodeSessionEvent[] }>(
        `${url}/events?after=${afterSeq}`,
        readInit(options),
      ),
    contextWindow: (options) =>
      codeSessionRequest<{ tokens: number | null }>(
        `${url}/context-window`,
        readInit(options),
      ).then(({ tokens }) => tokens),
    promptAuthor: (viewerProfileId) => viewerProfileId,
    prompt: async (input) => {
      const { turnId, text, delivery, skills, authoredText, attachments } = input;
      const sentAt = new Date().toISOString();
      try {
        const answer = await codeSessionRequest<{ turn: CodeSessionTurn; cutIn: boolean }>(
          `${url}/prompt`,
          jsonBody({
            turnId,
            text,
            delivery,
            authoredText,
            ...(skills.length ? { skills } : {}),
            ...(attachments.length ? { attachments: attachmentRefs(attachments) } : {}),
          }),
        );
        unconfirmed.drop(turnId);
        return answer;
      } catch (error) {
        if (isRefusal(error)) {
          throw error;
        }
        // The POST may have committed before its response was lost. Retain its identity/content.
        unconfirmed.add(turnId, unsentTurn({ ...input, sentAt }));
        throw new UnconfirmedPromptError();
      }
    },
    steerTurn: (turnId) =>
      codeSessionRequest<{ turn: CodeSessionTurn }>(`${url}/turns/${turnId}/steer`, {
        method: 'POST',
      }),
    cancelTurn: (turnId) =>
      codeSessionRequest<{ turn: CodeSessionTurn }>(`${url}/turns/${turnId}`, {
        method: 'DELETE',
      }),
    // Both lists go even when empty: the server replaces the turn's with each.
    editTurn: (turnId, { text, skills, attachments }) =>
      codeSessionRequest<{ turn: CodeSessionTurn }>(`${url}/turns/${turnId}`, {
        ...jsonBody({ text, skills, attachments: attachmentRefs(attachments) }),
        method: 'PATCH',
      }),
    moveTurn: (turnId, beforeTurnId) =>
      codeSessionRequest<{ turns: CodeSessionTurn[] }>(
        `${url}/turns/${turnId}/move`,
        jsonBody({ beforeTurnId }),
      ),
    sendNow: () =>
      codeSessionRequest<{ interrupted: boolean }>(`${url}/steer-now`, { method: 'POST' }),
    interrupt: () =>
      codeSessionRequest<{ interrupted: boolean }>(`${url}/interrupt`, { method: 'POST' }),
    answer: (formId, reply) =>
      codeSessionRequest<{ ok: true }>(
        `${url}/forms/${formId}`,
        jsonBody('cancel' in reply ? { cancel: true } : { answer: reply }),
      ),
    stageRewind: (turnId, restoreFiles) =>
      codeSessionRequest<{ session: CodeSessionRow }>(
        `${url}/turns/${turnId}/rewind`,
        jsonBody({ restoreFiles }),
      ),
    clearRewind: () =>
      codeSessionRequest<{ session: CodeSessionRow }>(`${url}/rewind`, { method: 'DELETE' }),
    fork: ({ beforeTurnId }) => {
      const forkId = crypto.randomUUID();
      const send = () =>
        codeSessionRequest<{ session: CodeSessionRow }>(
          `${url}/fork`,
          jsonBody(beforeTurnId === undefined ? { forkId } : { beforeTurnId, forkId }),
        );
      return {
        forkId,
        // The server answers a repeated fork ID with the fork it already made, so a
        // lost response or server error is retried once before the open fork closes.
        make: () =>
          send().catch(async (error: unknown) => {
            if (isRefusal(error)) {
              throw error;
            }
            await new Promise((resolve) => setTimeout(resolve, 300));
            return send();
          }),
      };
    },
    savedTurns: (options) =>
      withUnsentSends(unconfirmed, () =>
        codeSessionRequest<{ session: CodeSessionRow }>(`${url}/turns`, readInit(options)),
      ),
  };
}

/** `url` is the session's API path (`/api/code-sessions/<id>`). */
function cloudWorkspace(url: string): SessionWorkspace {
  return {
    file: (path, options) =>
      codeSessionRequest<WorkspaceFileResult>(
        `${url}/file?${new URLSearchParams({ path: normalizeWorkspacePath(path) })}`,
        readInit(options),
      ),
    changes: (options) =>
      codeSessionRequest<WorkspaceChangesResult>(`${url}/changes`, readInit(options)),
    diff: (path, base, options) =>
      codeSessionRequest<WorkspaceDiffResult>(
        `${url}/diff?${new URLSearchParams({ path: normalizeWorkspacePath(path), base })}`,
        readInit(options),
      ),
    gitStatus: (options) => codeSessionRequest<SessionGitStatus>(`${url}/git`, readInit(options)),
    gitAction: (input) => codeSessionRequest<{ success: true }>(`${url}/git`, jsonBody(input)),
    findFiles: (query, options) =>
      codeSessionRequest<WorkspaceSearchResult>(
        `${url}/files?${new URLSearchParams({ query })}`,
        readInit(options),
      ),
  };
}

function cloudRecord(url: string): SessionRecord {
  return {
    rename: async (title) => {
      const { session } = await codeSessionRequest<{ session: CodeSessionRow }>(`${url}/title`, {
        ...jsonBody({ title }),
        method: 'PATCH',
      });
      return { title: session.title, updatedAt: session.updatedAt };
    },
    setApprovalMode: async () => {
      throw new CodeSessionRequestError(
        "An approval mode is not available for a cloud session, which runs with cubic's own rules",
        400,
      );
    },
    regenerateTitle: async () => {
      const { session } = await codeSessionRequest<{ session: CodeSessionRow }>(`${url}/title`, {
        method: 'POST',
      });
      return { title: session.title, updatedAt: session.updatedAt };
    },
    archive: async () => {
      const { session } = await codeSessionRequest<{ session: CodeSessionRow }>(`${url}/archive`, {
        method: 'POST',
      });
      return session;
    },
    unarchive: async () => {
      const { session } = await codeSessionRequest<{ session: CodeSessionRow }>(
        `${url}/unarchive`,
        { method: 'POST' },
      );
      return session;
    },
  };
}

/** A session in a repository with its first prompt, as the composer's cloud target names it. */
export type CreateCloudSessionInput = Pick<CreateCodeSessionInput, 'model' | 'prompt'> &
  Pick<
    Extract<CreateCodeSessionTarget, { kind: 'cloud' }>,
    'installationId' | 'repositoryId' | 'attachments' | 'warmSessionId'
  >;

/** Sessions that run in cubic's cloud sandboxes, behind `/api/code-sessions`. */
export const cloudRuntime: CodeRuntime & {
  /** An installation's sessions, as the server lists them. */
  listSessions: (
    filter: { installationId: number },
    options?: ReadOptions,
  ) => Promise<CodeSessionSummary[]>;
  /** Start a session in a repository with its first prompt. */
  createSession: (
    input: CreateCloudSessionInput,
  ) => Promise<Pick<SessionResponse, 'session' | 'setupEvents'>>;
} = {
  session: (sessionId) => {
    const url = `/api/code-sessions/${encodeURIComponent(sessionId)}`;
    return {
      conversation: cloudConversation(url, unconfirmedSendsOf(sessionId)),
      workspace: cloudWorkspace(url),
      record: cloudRecord(url),
      skills: (options) =>
        codeSessionRequest<CodeSkillInventory>(`${url}/skills`, readInit(options)),
      retry: (next) => {
        next();
      },
    };
  },
  queue: 'turns',
  words: {
    resuming: 'Waking up environment…',
    ownTitle: ({ title }) => title,
  },
  forget: (sessionId) => {
    unconfirmedSends.delete(sessionId);
  },
  listSessions: async ({ installationId }, options) => {
    const body = await codeSessionRequest<{ sessions: CodeSessionSummary[] }>(
      `/api/code-sessions?installationId=${installationId}`,
      readInit(options),
    );
    return body.sessions;
  },
  createSession: ({ attachments, warmSessionId, ...input }) =>
    codeSessionRequest<Pick<SessionResponse, 'session' | 'setupEvents'>>(
      '/api/code-sessions',
      jsonBody({
        ...input,
        ...(attachments.length ? { attachments: attachmentRefs(attachments) } : {}),
        ...(warmSessionId === null ? {} : { warmSessionId }),
      }),
    ),
};
